import "./Header.scss";

import Button from "components/Button/Button";
import SearchBar from "components/SearchBar/SearchBar";
import FinanceReportHeaderStatus from "pages/finance/components/FinanceReportHeaderStatus";
import { useLienFileData } from "pages/liens/hooks/useLienFileData";
import { useEffect, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { setFileId } from "reducers/files";
import { setShowSubHeader } from "reducers/main";
import { useCreateFileMutation, useGetFileByIdQuery } from "services/file-api";
import { useGetMiscServiceByIdQuery } from "services/misc-api";
import type { RootState } from "services/store";
import type {
  AmendedReviewScreeningType,
  CostProjectionDataType,
  DocumentManagementDataType,
  MSADataType,
} from "types/DataTypes";
import { useConstants } from "utils/hooks";
import { useDispatch, useSelector } from "utils/redux";

import FinanceFilterContainer from "./FinanceFilter/FinanceFilterContainer";
import { ROUTE_CONFIG } from "./HeaderRoutes";
import HeaderInfoPill from "./InfoPill/InfoPill";
import {
  getClaimInfoPillData,
  getRREInfoPillData,
} from "./InfoPill/utils/ClaimInfoPill";
import { getDocumentManagementInfoPillData } from "./InfoPill/utils/DMInfoPill";
import { getFileInfoPillData } from "./InfoPill/utils/FileInfoPill";
import {
  getAmendedReviewScreeningInfoPillData,
  getMedicalServiceInfoPillData,
  getOverviewDataForInfoPill,
} from "./InfoPill/utils/FMInfoPill";
import {
  getLienDetailInfoPillData,
  getLienFileInfoPillData,
} from "./InfoPill/utils/LienInfoPill";

type RouteParams = {
  claimId?: string;
  fileId?: string;
  lienId?: string;
  miscServiceId?: string;
};

export const Header = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { constants } = useConstants();

  const {
    fileId: fileIdFromParams,
    lienId,
    miscServiceId,
  } = useParams<RouteParams>();

  // get file_id from query params
  const queryParams = new URLSearchParams(location.search);
  const fileIdFromQuery = queryParams.get("file_id");

  // use file_id from query params if available, otherwise use from route params
  const fileId = fileIdFromQuery || fileIdFromParams;

  const claim = useSelector(
    (state: RootState) => state.claimsForm.currentClaim,
  );

  const rre = useSelector((state: RootState) => state.rre.currentRRE);
  const currentClient = useSelector(
    (state: RootState) => state.client.currentClient,
  );
  const currentFile = useSelector(
    (state: RootState) => state.files.currentFile,
  );

  const currentService = useSelector(
    (state: RootState) => state.futureMedical.currentService,
  );

  const { data: miscServiceData } = useGetMiscServiceByIdQuery(
    { id: miscServiceId || "" },
    { skip: !miscServiceId },
  );

  const { lienFileData, isLoading: isLienFileLoading } = useLienFileData();

  const { data: fileData, isLoading: isFileLoading } = useGetFileByIdQuery(
    { id: fileId || "" },
    { skip: !fileId },
  );

  const [createFile] = useCreateFileMutation();

  const client = currentClient || undefined;

  // to debug, just log the route object below
  const currentRoute = useMemo(() => {
    const route = ROUTE_CONFIG.find((route) => {
      const pathPattern = new RegExp(
        `^${route.path.replace(/:[^\s/]+/g, "[\\w-]+")}$`,
      );
      const matches = pathPattern.test(location.pathname);
      return matches;
    });
    return route;
  }, [location.pathname]);

  // determine filterType based on current route
  const getFilterTypeForRoute = ():
    | "files"
    | "claims"
    | "rres"
    | "your-tasks"
    | "operations"
    | "documents" => {
    if (location.pathname === "/" || location.pathname === "/your-tasks")
      return "your-tasks";
    if (location.pathname.includes("/section-111/claims")) return "claims";
    if (location.pathname.includes("/section-111/rres")) return "rres";
    if (location.pathname.includes("/operations/files")) return "files";
    if (location.pathname.includes("/operations/intake")) return "files"; // intake uses same filters as files
    if (location.pathname.includes("/operations/")) return "operations";

    // default fallback
    return "claims";
  };

  const filterType = getFilterTypeForRoute();

  const isNewFileView = location.pathname.endsWith("/files/new");

  useEffect(() => {
    dispatch(setShowSubHeader(isNewFileView));
  }, [isNewFileView, dispatch]);

  const handleCreateFile = async () => {
    if (!currentFile) return;

    try {
      const response = await createFile({
        data: currentFile as unknown as FormData,
      }).unwrap();
      dispatch(setFileId(Number(response.id)));
    } catch (error) {
      console.error(error);
    }
  };

  // determine rush type with priority: 24-hour > 3-day > general rush
  const getRushType = (file?: {
    is_24_hour_rush?: boolean;
    is_3_day_rush?: boolean;
    is_rush?: boolean;
  }): "24-hour" | "3-day" | "rush" | null => {
    if (!file) return null;
    if (file.is_24_hour_rush) return "24-hour";
    if (file.is_3_day_rush) return "3-day";
    if (file.is_rush) return "rush";
    return null;
  };

  const renderHeaderInfo = () => {
    if (!currentRoute?.showHeaderInfo) return null;

    switch (currentRoute.headerType) {
      case "claim":
        return (
          claim && (
            <HeaderInfoPill
              data={getClaimInfoPillData(claim)}
              className="section-111"
            />
          )
        );
      case "rre":
        return (
          rre && (
            <HeaderInfoPill
              data={getRREInfoPillData(rre)}
              className="section-111"
            />
          )
        );
      case "file":
        return (
          !isFileLoading && (
            <HeaderInfoPill
              data={getFileInfoPillData(isNewFileView, client, fileData)}
              className="operations"
              rushType={getRushType(fileData)}
            />
          )
        );
      case "lien-overview":
        return (
          !isLienFileLoading && (
            <HeaderInfoPill
              data={getLienFileInfoPillData(lienFileData, fileData)}
              className="liens"
              rushType={getRushType(fileData)}
            />
          )
        );
      case "lien-detail":
        return (
          !isLienFileLoading && (
            <HeaderInfoPill
              data={getLienDetailInfoPillData(lienFileData, lienId, constants)}
              className="liens"
              rushType={getRushType(fileData)}
            />
          )
        );
      case "fm-overview":
        return (
          <HeaderInfoPill
            data={getOverviewDataForInfoPill(fileData)}
            className="future-medical"
            rushType={getRushType(fileData)}
          />
        );
      case "arScreening":
        return (
          currentService && (
            <HeaderInfoPill
              data={getAmendedReviewScreeningInfoPillData(
                currentService as AmendedReviewScreeningType,
              )}
              className="future-medical"
              rushType={getRushType(
                (currentService as AmendedReviewScreeningType)?.file,
              )}
            />
          )
        );
      case "costProjection":
      case "msa":
        return (
          <HeaderInfoPill
            data={getMedicalServiceInfoPillData(
              currentService as MSADataType | CostProjectionDataType,
            )}
            className="future-medical"
            rushType={getRushType(
              (currentService as MSADataType | CostProjectionDataType)?.file,
            )}
          />
        );
      case "document-management":
        return (
          <HeaderInfoPill
            data={getDocumentManagementInfoPillData(
              currentService as DocumentManagementDataType,
            )}
            className="document-management"
            rushType={getRushType(
              (currentService as DocumentManagementDataType)?.file,
            )}
          />
        );
      case "audit":
      case "consulting":
        return (
          miscServiceData && (
            <HeaderInfoPill
              data={getFileInfoPillData(false, undefined, miscServiceData.file)}
              className="operations"
              rushType={getRushType(miscServiceData.file)}
            />
          )
        );
      case "finance-filter":
        return (
          <FinanceFilterContainer
            reportStatus={<FinanceReportHeaderStatus />}
          />
        );
      default:
        return null;
    }
  };

  return (
    <header className="header">
      <div className="header-logo" onClick={() => navigate("/")}>
        <div className="header-image">
          <img src="/static/images/logo-purple.png"></img>
        </div>
      </div>

      {currentRoute?.showSearch && (
        <SearchBar isHidden={false} filterType={filterType} />
      )}
      {renderHeaderInfo()}

      {isNewFileView && (
        <div className="flex jc-end">
          <Button onClick={handleCreateFile}>Complete File Intake</Button>
        </div>
      )}
    </header>
  );
};

export default Header;
